import type { Pointer, TemplateExercise, WorkoutTemplate } from './types.js';

export interface WorkoutGroup {
  key: string;
  exercises: TemplateExercise[];
}

export const WORKOUT_REST_SECONDS = 60;

export function buildGroups(template: WorkoutTemplate): WorkoutGroup[] {
  const groups = new Map<string, TemplateExercise[]>();
  for (const item of [...template.exercises].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const existing = groups.get(item.supersetGroup) ?? [];
    existing.push(item);
    groups.set(item.supersetGroup, existing);
  }
  return [...groups.entries()].map(([key, exercises]) => {
    exercises.sort((a, b) => a.positionInPair - b.positionInPair);
    return {
      key,
      exercises,
    };
  });
}

export function initialPointer(): Pointer {
  return { groupIndex: 0, round: 1, position: 0, restEndAt: null };
}

export function currentExercise(template: WorkoutTemplate, pointer: Pointer): TemplateExercise | null {
  if (pointer.selectionRequired) return null;
  const group = buildGroups(template)[pointer.groupIndex];
  if (!group) return null;
  return group.exercises[pointer.position] ?? null;
}

export function orderedExercises(template: WorkoutTemplate) {
  return buildGroups(template).flatMap((group) => group.exercises);
}

export function pointerForExercise(
  template: WorkoutTemplate,
  templateExerciseId: string,
  setNumber = 1,
): Pointer | null {
  const groups = buildGroups(template);
  const groupIndex = groups.findIndex((group) =>
    group.exercises.some((exercise) => exercise.id === templateExerciseId),
  );
  const position = groups[groupIndex]?.exercises.findIndex(
    (exercise) => exercise.id === templateExerciseId,
  );
  return groupIndex >= 0 && position !== undefined && position >= 0
      ? {
        groupIndex,
        position,
        round: setNumber,
        restEndAt: null,
        selectionRequired: false,
      }
    : null;
}

export interface CompletedSetSlot {
  templateExerciseId: string;
  setNumber: number;
}

function completedNumbers(
  templateExerciseId: string,
  completed: CompletedSetSlot[],
): Set<number> {
  return new Set(
    completed
      .filter((set) => set.templateExerciseId === templateExerciseId)
      .map((set) => set.setNumber),
  );
}

export function nextMissingExerciseSet(
  exercise: TemplateExercise,
  completed: CompletedSetSlot[],
): number | null {
  if (exercise.finishedEarly) return null;
  const occupied = completedNumbers(exercise.id, completed);
  for (let setNumber = 1; setNumber <= exercise.sets; setNumber += 1)
    if (!occupied.has(setNumber)) return setNumber;
  return null;
}

/**
 * If legacy/corrupt data has several partial exercises, the first one in the
 * displayed routine order wins deterministically. No valid set is discarded.
 */
export function activeSeriesExercise(
  template: WorkoutTemplate,
  completed: CompletedSetSlot[],
): TemplateExercise | null {
  return (
    orderedExercises(template).find((exercise) => {
      const occupied = completedNumbers(exercise.id, completed);
      return occupied.size > 0 && nextMissingExerciseSet(exercise, completed) !== null;
    }) ?? null
  );
}

/**
 * Keep the user's selection, restoring that exercise's next missing set.
 * A done exercise can still be selected to review or edit its logged sets.
 */
export function reconcileWorkoutPointer(
  template: WorkoutTemplate,
  completed: CompletedSetSlot[],
  current: Pointer,
): Pointer {
  if (current.selectionRequired)
    return { ...current, ...(current.exerciseTimer ? { exerciseTimer: null } : {}), selectionRequired: true };
  const selected = currentExercise(template, current);
  const target = selected;
  if (!target)
    return { ...current, restEndAt: null, selectionRequired: true };
  const nextSet = nextMissingExerciseSet(target, completed);
  if (nextSet === null) return { ...current, ...(current.exerciseTimer ? { exerciseTimer: null } : {}) };
  const expected = pointerForExercise(template, target.id, nextSet);
  if (!expected) return current;
  const isSameSlot =
    current.groupIndex === expected.groupIndex &&
    current.position === expected.position &&
    current.round === expected.round;
  return {
    ...expected,
    ...(isSameSlot && !current.restEndAt && target.exercise.measurement === 'seconds' && current.exerciseTimer?.slotId === target.id && current.exerciseTimer.setNumber === nextSet
      ? { exerciseTimer: current.exerciseTimer } : {}),
    restEndAt: isSameSlot ? current.restEndAt : null,
    selectionRequired: false,
  };
}

export interface AdvanceResult {
  pointer: Pointer;
  startRestSeconds: number | null;
  finished: boolean;
}

export function advanceAfterSet(
  template: WorkoutTemplate,
  pointer: Pointer,
  now = Date.now(),
  completed: CompletedSetSlot[] = [],
): AdvanceResult {
  pointer = { ...pointer };
  delete pointer.exerciseTimer;
  const ordered = orderedExercises(template);
  const selected = currentExercise(template, pointer);
  const currentIndex = ordered.findIndex(
    (entry) => entry.id === selected?.id,
  );
  if (!selected || currentIndex < 0)
    return { pointer, startRestSeconds: null, finished: true };

  let nextPointer: Pointer | null = null;
  const nextSet = completed.length ? nextMissingExerciseSet(selected, completed)
    : pointer.round < selected.sets ? pointer.round + 1 : null;
  if (nextSet !== null) {
    nextPointer = { ...pointer, round: nextSet, restEndAt: null };
  } else {
    const remaining = completed.length
      ? ordered.some((exercise) => completedNumbers(exercise.id, completed).size < exercise.sets)
      : Boolean(ordered[currentIndex + 1]);
    if (remaining) {
      const restSeconds = completed.length && !ordered.some(exercise => nextMissingExerciseSet(exercise, completed) !== null) ? null : WORKOUT_REST_SECONDS;
      return {
        pointer: {
          ...pointer,
          restEndAt: restSeconds ? new Date(now + restSeconds * 1000).toISOString() : null,
          selectionRequired: true,
        },
        startRestSeconds: restSeconds,
        finished: false,
      };
    }
  }
  if (!nextPointer) {
    return { pointer: { ...pointer, restEndAt: null }, startRestSeconds: null, finished: true };
  }
  return {
    pointer: {
      ...nextPointer,
      restEndAt: new Date(now + WORKOUT_REST_SECONDS * 1000).toISOString(),
    },
    startRestSeconds: WORKOUT_REST_SECONDS,
    finished: false,
  };
}

export function clearRest(pointer: Pointer): Pointer {
  return { ...pointer, restEndAt: null };
}

export function adjustRest(pointer: Pointer, seconds: number): Pointer {
  const base = pointer.restEndAt ? new Date(pointer.restEndAt).getTime() : Date.now();
  return { ...pointer, restEndAt: new Date(Math.max(Date.now(), base + seconds * 1000)).toISOString() };
}

export function totalPrescribedSets(template: WorkoutTemplate): number {
  return template.exercises.reduce((total, exercise) => total + exercise.sets, 0);
}

export function isProgressionEligible(reps: Array<number | null>, prescribedSets: number, repMax = 12): boolean {
  return reps.length === prescribedSets && reps.every((value) => value !== null && value >= repMax);
}

export function nextRotationOrder(current: number, total = 4): number {
  return current >= total ? 1 : current + 1;
}
